<template>
  <div class="global-pagination-wrapper">

    <el-pagination v-model:current-page="currentPageValue" v-model:page-size="pageSizeValue" :page-sizes="pageSizes"
      :size="size" :disabled="disabled" :hide-on-single-page="hideOnSinglePage" :total="total" :layout="layout"
      :prev-text="prevText" :next-text="nextText" :background="background" :small="small"
      @size-change="handleSizeChange" @current-change="handleCurrentChange" @prev-click="handlePrevClick"
      @next-click="handleNextClick" />

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { ComponentSize } from 'element-plus'
// 定义组件属性
interface Props {
  // 当前页码
  currentPage?: number
  // 每页显示条目个数
  pageSize?: number
  // 总条目数
  total: number
  // 每页显示个数选择器的选项设置
  pageSizes?: number[]
  // 组件大小
  size?: ComponentSize
  // 是否禁用
  disabled?: boolean
  // 只有一页时是否隐藏
  hideOnSinglePage?: boolean
  // 组件布局，子组件名用逗号分隔
  layout?: string
  // 替代图标显示的上一页文字
  prevText?: string
  // 替代图标显示的下一页文字
  nextText?: string
  // 是否为分页按钮添加背景色
  background?: boolean
  // 是否使用小型分页样式
  small?: boolean
  // 是否显示总条目数
  showTotal?: boolean
  // 是否显示页码跳转器
  showJumper?: boolean
}

// 设置默认值
const props = withDefaults(defineProps<Props>(), {
  currentPage: 1,
  pageSize: 10,  // 修改默认每页条数为20
  pageSizes: () => [10, 20, 50, 100],
  size: 'default',
  disabled: false,
  hideOnSinglePage: false,
  layout: 'total, sizes, prev, pager, next, jumper',
  prevText: '',
  nextText: '',
  background: true,
  small: false,
  showTotal: true,
  showJumper: true
})

// 定义事件
interface Emits {
  'update:currentPage': [page: number]
  'update:pageSize': [size: number]
  'size-change': [size: number]
  'current-change': [page: number]
  'prev-click': [page: number]
  'next-click': [page: number]
}

const emit = defineEmits<Emits>()

// 响应式数据
const currentPageValue = ref(props.currentPage)
const pageSizeValue = ref(props.pageSize)
const jumpPage = ref(props.currentPage)

// 计算属性
const totalPages = computed(() => {
  return Math.max(1, Math.ceil(props.total / pageSizeValue.value))
})

// 监听props变化
watch(() => props.currentPage, (newVal) => {
  currentPageValue.value = newVal
  jumpPage.value = newVal
})

watch(() => props.pageSize, (newVal) => {
  pageSizeValue.value = newVal
})

// 事件处理函数
const handleSizeChange = (size: number) => {
  emit('update:pageSize', size)
  emit('size-change', size)
  // 当改变每页显示数量时，重置到第一页
  if (currentPageValue.value > totalPages.value) {
    currentPageValue.value = 1
    emit('update:currentPage', 1)
    emit('current-change', 1)
  }
}

const handleCurrentChange = (page: number) => {
  emit('update:currentPage', page)
  emit('current-change', page)
  jumpPage.value = page
}

const handlePrevClick = (page: number) => {
  emit('prev-click', page)
}

const handleNextClick = (page: number) => {
  emit('next-click', page)
}

const handleJumpPage = (page: number | undefined) => {
  if (page && page >= 1 && page <= totalPages.value && page !== currentPageValue.value) {
    currentPageValue.value = page
    emit('update:currentPage', page)
    emit('current-change', page)
  }
}
</script>

<style lang="less" scoped>
::v-deep .el-select-dropdown__item li {
  background-color: transparent !important;

}

// prev和next箭头的样式
::v-deep .el-pagination .btn-next,
::v-deep .el-pagination .btn-prev {
  background: transparent !important;
  background-color: transparent !important;
}

// prev和next箭头disabled的样式
::v-deep .el-pagination button:disabled {
  background-color: transparent !important;
}

// 页码样式
::v-deep .el-pager li {
  background-color: transparent !important;
  font-size: 16px !important;
  color: #888888 !important;

}

// active的页码样式
::v-deep .el-pager li.is-active {
  color: #267aff !important;
}

::v-deep .el-select .el-select__placeholder {
  // --el-text-color-regular: #888888 !important;
  color: #888888 !important;
}

// :
// .el-pagination__total
::v-deep .el-pagination__total {
  color: #888888 !important;
  font-size: 13px !important;
}


.global-pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 0;

  flex-shrink: 0;
  height: 50px;

  .pagination-info {
    .total-text {
      font-size: 13px;
      color: #606266;
      font-weight: 400;
    }
  }

  .global-pagination {
    :deep(.el-pagination__total) {
      font-size: 14px;
      color: #606266;
      margin-right: 16px;
    }

    :deep(.el-pagination__sizes) {
      margin-right: 16px;

      .el-select {
        .el-select__wrapper {
          min-width: 100px;
        }
      }
    }

    ::deep(.el-pager) {
      li {
        min-width: 32px;
        height: 32px;
        line-height: 30px;
        margin: 0 2px;
        border-radius: 4px;
        // font-size: 20px !important;
        color: #606266;
        // background-color: #f5f7fa;
        border: 1px solid #dcdfe6;
        transition: all 0.3s;
        // background: transparent !important;
        --el-fill-color-blank: red !important;

        &:hover {
          color: #409eff;
          background-color: #ecf5ff;
          border-color: #b3d8ff;
        }

        &.is-active {
          color: wheat !important;
          background-color: transparent !important;
          border-color: #409eff;
        }

        &.disabled {
          color: #c0c4cc;
          background-color: #f5f7fa;
          border-color: #dcdfe6;
          cursor: not-allowed;
        }

        // --el-pagination-button-bg-color: transparent !important;
      }
    }

    :deep(.btn-prev),
    :deep(.btn-next) {
      min-width: 32px;
      height: 32px;
      line-height: 30px;
      margin: 0 2px;
      border-radius: 4px;
      font-size: 14px;
      color: #606266;
      background-color: #f5f7fa !important;
      border: 1px solid #dcdfe6;
      transition: all 0.3s;

      &:hover {
        color: red;
        background-color: #ecf5ff;
        border-color: #b3d8ff;
      }

      &:disabled {
        color: #c0c4cc;
        background-color: #f5f7fa;
        border-color: #dcdfe6;
        cursor: not-allowed;
      }
    }

    :deep(.el-pagination__jump) {
      margin-left: 16px;

      .el-input {
        width: 50px;
        margin: 0 8px;

        .el-input__wrapper {
          height: 32px;
        }
      }
    }
  }

  .pagination-jumper {
    display: flex;
    align-items: center;
    gap: 8px;

    .jumper-text {
      font-size: 14px;
      color: #606266;
    }

    .jumper-input {
      width: 60px;

      :deep(.el-input__wrapper) {
        height: 32px;
      }

      :deep(.el-input-number__decrease),
      :deep(.el-input-number__increase) {
        width: 24px;
        height: 16px;
        line-height: 16px;
      }
    }
  }
}

.global-pagination {
  ::v-deep(.btn-prev) {
    background-color: transparent !important;
    border: none !important;
  }

  ::v-deep(.btn-next) {
    background-color: transparent !important;
    border: none !important;
  }

  ::v-deep(.el-pager) {

    li {
      min-width: 32px;
      height: 32px;
      border-radius: 4px;
      background-color: transparent !important;
      border: none !important;

      &:hover {
        color: #409eff;
        background-color: transparent !important;
      }

      &.is-active {
        color: #409eff !important;
        background-color: none !important;
      }
    }
  }

  // 修改上一页/下一页按钮
  :deep(.btn-prev),
  :deep(.btn-next) {
    min-width: 32px;
    height: 32px;
    background-color: #f5f7fa;
  }

  // 修改总条数显示
  :deep(.el-pagination__total) {
    font-size: 14px;
    color: #606266;
  }

  // 修改每页条数选择器
  :deep(.el-pagination__sizes) {
    .el-select__wrapper {
      min-width: 100px;
    }
  }
}

// 小尺寸样式
.global-pagination-wrapper.small {
  .global-pagination {
    :deep(.el-pager) {
      li {
        min-width: 28px;
        height: 28px;
        line-height: 26px;
        font-size: 12px;
      }
    }

    :deep(.btn-prev),
    :deep(.btn-next) {
      min-width: 28px;
      height: 28px;
      line-height: 26px;
      font-size: 12px;
    }
  }

  .pagination-jumper {
    .jumper-input {
      :deep(.el-input__wrapper) {
        height: 28px;
      }
    }
  }
}

// 大尺寸样式
.global-pagination-wrapper.large {
  .global-pagination {
    :deep(.el-pager) {
      li {
        min-width: 36px;
        height: 36px;
        line-height: 34px;
        font-size: 16px;
      }
    }

    :deep(.btn-prev),
    :deep(.btn-next) {
      min-width: 36px;
      height: 36px;
      line-height: 34px;
      font-size: 16px;
    }
  }

  .pagination-jumper {
    .jumper-input {
      :deep(.el-input__wrapper) {
        height: 36px;
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .global-pagination-wrapper {
    flex-direction: column;
    gap: 12px;

    .global-pagination {
      :deep(.el-pagination__sizes) {
        margin-right: 8px;
      }

      :deep(.el-pagination__jump) {
        margin-left: 8px;
      }
    }
  }
}
</style>